import React, { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import NFTCard from "../../components/NFTCard/NFTCard";
import Pagination from "../../components/Pagination/Pagination";
import SkeletonNFTCard, {
  SkeletonListItem,
} from "../../components/SkeletonNFTCard/SkeletonNFTCard";
import Meta from "../../components/Meta";
import MarketplaceSortFilter from "../../components/impact-marketplace/MarketplaceSortFilter";
import Sidebar from "../../components/collections-wide/sidebar";
import Collections from "../../components/collections-wide/collection-content";
import { useNFTMarketplace } from "../../contexts/NFTMarketplaceContext";
import { recordBlockchainPurchase } from "../../services/payment";
import { errorToast, successToast } from "../../utils/toast";
import { mapWeb3Error } from "../../utils/web3ErrorMessage";

const ITEMS_PER_PAGE = 60;

const initialFilters = {
  isSold: false,
  priceMin: "",
  priceMax: "",
  percentageMin: 0,
  percentageMax: 100,
  impactCategory: "",
  nftCategory: "",
  username: "",
  itemName: "",
  campaignName: "",
  sortBy: "recently_created",
};

const MarketplacePage = () => {
  // NFT Marketplace Context
  const { fetchNFTs, buyNFT, isWalletConnected, walletAddress } =
    useNFTMarketplace();

  // State for fetched data
  const [nfts, setNfts] = useState([]);
  const [blockchainNfts, setBlockchainNfts] = useState([]);
  const [cardListingNfts, setCardListingNfts] = useState([]);

  // Tab state: 0 = Impact Art, 1 = On-Chain
  const [activeTab, setActiveTab] = useState(0);
  const [onChainPage, setOnChainPage] = useState(1);
  const [nftCategories, setNftCategories] = useState([]);
  const [impactCategories, setImpactCategories] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [blockchainLoading, setBlockchainLoading] = useState(false);
  const [error, setError] = useState(null);
  const [purchaseModal, setPurchaseModal] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  // State for the filter inputs
  const [filters, setFilters] = useState(initialFilters);
  // Separate state for applied filters vs current input values
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'

  // Fetch blockchain NFTs
  const fetchBlockchainNFTs = useCallback(async () => {
    try {
      setBlockchainLoading(true);
      const blockchainAssets = await fetchNFTs();

      if (!blockchainAssets || blockchainAssets.length === 0) {
        setBlockchainNfts([]);
        return [];
      }

      // Transform blockchain NFTs to match database NFT structure
      const transformedBlockchainNfts = blockchainAssets.map((nft) => ({
        // Additive 3-part id carries the token's OWN contract so the detail
        // page and buy flow route to the right marketplace (V1 vs V2). Legacy
        // 2-part ids still resolve to V1 on the detail page.
        _id: `blockchain_${nft.contractAddress || ""}_${nft.tokenId}`,
        tokenId: nft.tokenId,
        contractAddress: nft.contractAddress,
        itemName: nft.name || "Untitled Impact Art",
        description: nft.description || "On-chain Impact Art from blockchain",
        imageUrl: nft.image, // Full IPFS URL from response
        priceEth: nft.price || "0.001",
        creator: {
          username:
            nft.impactData?.creator ||
            (nft.seller
              ? `${nft.seller.slice(0, 6)}...${nft.seller.slice(-4)}`
              : "Unknown Creator"),
          walletAddress: nft.seller,
        },
        seller: nft.seller,
        owner: nft.owner,
        // A live on-chain reservation (someone's card checkout of a
        // dual-rail piece is in flight) — crypto buys revert while it holds
        locked: Boolean(nft.locked),
        // Raw split facts, carried so a grid purchase can record itself the
        // same way the detail page does (recordBlockchainPurchase)
        campaignOwner: nft.campaignOwner,
        causeOwner: nft.causeOwner,
        rawImpactPercentage: nft.impactPercentage,
        impactInitiativeName: nft.impactData?.initiative || "",
        viewsCount: 0,
        favouritesCount: 0,
        isBlockchain: true,
        nftCategory: nft.impactData?.nftCategory || "Digital Art",
        impactCategory: nft.impactData?.category || "General",
        percentageToImpact:
          // Blockchain impactPercentage is in basis points (2500 = 25%)
          nft.impactPercentage
            ? Math.round(nft.impactPercentage / 100)
            : parseInt(nft.impactData?.allocationPercentage) || 25,
        createdAt: nft.created_at || new Date().toISOString(),
        updatedAt: nft.created_at || new Date().toISOString(),
      }));

      setBlockchainNfts(transformedBlockchainNfts);
      return transformedBlockchainNfts;
    } catch (error) {
      // Blockchain NFTs are optional — don't surface to UI
      return [];
    } finally {
      setBlockchainLoading(false);
    }
  }, [fetchNFTs]);

  // Fetch v2 card-sale listings (on-chain art bought with a card). They live
  // in the backend registry, not on the v1 contract fetchNFTs() reads, so
  // they need their own source. Priced in USD, action is always the detail
  // page — never the v1 crypto buy flow.
  const fetchCardListings = useCallback(async () => {
    try {
      const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
      const res = await axios.get(
        `${baseUrl}/api/user/onchain-assets/card-listings`,
      );
      const listings =
        res.data?.success && Array.isArray(res.data.listings)
          ? res.data.listings
          : [];

      const transformed = listings.map((listing) => ({
        _id: `cardsale_${listing.registrationId}`,
        registrationId: listing.registrationId,
        tokenId: listing.tokenId,
        contractAddress: listing.contractAddress,
        // "fiat" = card-only (renders as its own tile); "both" = also a live
        // crypto listing (merged onto the crypto tile, see onChainItems).
        saleRail: listing.saleRail || "fiat",
        itemName: listing.itemName || "Untitled Impact Art",
        description: "On-chain Impact Art available by card",
        imageUrl: listing.imageUrl,
        priceUsd: listing.cardSalePriceUsd,
        isCardSale: true,
        ownerId: listing.ownerId,
        saleInProgress: Boolean(listing.saleInProgress),
        creator: { username: listing.sellerName || "Unknown Creator" },
        viewsCount: 0,
        favouritesCount: 0,
        nftCategory: "Digital Art",
        impactCategory: "General",
        percentageToImpact: listing.percentageToImpact || 0,
        createdAt: listing.createdAt || new Date().toISOString(),
        updatedAt: listing.createdAt || new Date().toISOString(),
      }));

      setCardListingNfts(transformed);
      return transformed;
    } catch (error) {
      // Card listings are optional on this page — treat as none
      setCardListingNfts([]);
      return [];
    }
  }, []);

  // Sort comparator function for client-side sorting (used for blockchain NFTs)
  const sortComparator = useCallback((a, b, sortBy) => {
    switch (sortBy) {
      case "recently_created":
        return new Date(b.createdAt) - new Date(a.createdAt);

      case "price_low_to_high": {
        const priceA = parseFloat(a.priceUsd) || 0;
        const priceB = parseFloat(b.priceUsd) || 0;
        return priceA - priceB;
      }

      case "price_high_to_low": {
        const priceA = parseFloat(a.priceUsd) || 0;
        const priceB = parseFloat(b.priceUsd) || 0;
        return priceB - priceA;
      }

      case "most_viewed": {
        const viewsA = parseInt(a.viewsCount) || 0;
        const viewsB = parseInt(b.viewsCount) || 0;
        return viewsB - viewsA;
      }

      case "alphabetical_a_z": {
        const nameA = (a.itemName || "").toLowerCase();
        const nameB = (b.itemName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      }

      case "alphabetical_z_a": {
        const nameA = (a.itemName || "").toLowerCase();
        const nameB = (b.itemName || "").toLowerCase();
        return nameB.localeCompare(nameA);
      }

      default:
        return 0;
    }
  }, []);

  // --- Fetch Data (depends on activeFilters and currentPage) ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Calculate offset based on current page (API expects page number, not raw skip value)
        const offset = currentPage - 1; // 0-based page number

        // Prepare the filter parameters for the API call
        const apiParams = {
          ...appliedFilters,
          limit: ITEMS_PER_PAGE,
          offset: offset,
          priceMin:
            appliedFilters.priceMin === ""
              ? 0
              : parseFloat(appliedFilters.priceMin) || 0,
          priceMax:
            appliedFilters.priceMax === ""
              ? 100000000
              : parseFloat(appliedFilters.priceMax) || 100000000,
        };

        const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
        const nftCategoriesUrl = `${baseUrl}/api/app/categories?kind=NFT`;
        const impactCategoriesUrl = `${baseUrl}/api/app/categories?kind=CAMPAIGN`;

        // Fetch all data concurrently - include sortBy in API params for server-side sorting
        const [assetsRes, nftCatRes, impactCatRes] = await Promise.all([
          axios.get(`${baseUrl}/api/app/list-assets`, {
            params: apiParams,
          }),
          axios.get(nftCategoriesUrl),
          axios.get(impactCategoriesUrl),
        ]);

        // Parse data
        const assetsData = assetsRes.data;
        const nftCatData = nftCatRes.data;
        const impactCatData = impactCatRes.data;

        // Validate data structure (basic check)
        if (!assetsData.success || !Array.isArray(assetsData.assetList)) {
          throw new Error("Invalid assets data structure received from API.");
        }
        if (!nftCatData.success || !Array.isArray(nftCatData.categoriesList)) {
          throw new Error(
            "Invalid Impact Art categories data structure received from API.",
          );
        }
        if (
          !impactCatData.success ||
          !Array.isArray(impactCatData.categoriesList)
        ) {
          throw new Error(
            "Invalid impact categories data structure received from API.",
          );
        }

        // Update state - server handles sorting, no need for frontend re-sort
        setNfts(assetsData.assetList || []);
        setTotalItems(
          assetsData.assetCount || assetsData.assetList?.length || 0,
        );
        setNftCategories(nftCatData.categoriesList || []);
        setImpactCategories(impactCatData.categoriesList || []);
      } catch (err) {
        const isNetworkError = err?.code === "ERR_NETWORK" || err?.message === "Network Error";
        if (!isNetworkError) console.error("Error fetching marketplace data:", err);
        setError(
          isNetworkError
            ? "Cannot connect to the server. Please check your connection and try again."
            : err.message || "Failed to load marketplace data."
        );
        setNfts([]);
        setNftCategories([]);
        setImpactCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appliedFilters, currentPage]);

  // Track when filters change
  useEffect(() => {
    const hasChanges =
      JSON.stringify(filters) !== JSON.stringify(appliedFilters);
    setFiltersChanged(hasChanges);
  }, [filters, appliedFilters]);

  // Fetch blockchain NFTs on mount so the tab count is accurate
  // The context handles caching (sessionStorage, 5-min TTL) so subsequent loads are instant
  useEffect(() => {
    if (typeof window === "undefined") return;
    fetchBlockchainNFTs();
    fetchCardListings();
  }, [fetchBlockchainNFTs, fetchCardListings]);

  // Normalize database NFTs with seller/owner info for ownership checks
  const normalizedDatabaseNfts = useMemo(() => {
    return nfts.map((nft) => ({
      ...nft,
      // Keep the owning user's id around — the owner/seller fields below get
      // flattened to wallet-address strings for the blockchain-style checks
      ownerId: nft.owner?.ownerId || null,
      seller:
        (typeof nft.seller === "string" ? nft.seller : null) ||
        (typeof nft.creator?.walletAddress === "string"
          ? nft.creator.walletAddress
          : null) ||
        (typeof nft.owner === "string" ? nft.owner : null),
      owner:
        (typeof nft.owner === "string" ? nft.owner : null) ||
        (typeof nft.creator?.walletAddress === "string"
          ? nft.creator.walletAddress
          : null) ||
        (typeof nft.seller === "string" ? nft.seller : null),
    }));
  }, [nfts]);

  // On-Chain tab shows two sources: crypto listings (from the chain) and
  // card-sale listings (from the backend registry). Card-only ("fiat")
  // pieces are their own tiles. A dual-rail ("both") piece appears in BOTH
  // sources — the same token is a live crypto listing AND a card listing —
  // so it must render ONCE: we enrich its crypto tile with the card terms
  // and drop the duplicate card row.
  const onChainItems = useMemo(() => {
    // Index card listings by the token's identity (contract + tokenId).
    const cardByToken = new Map();
    cardListingNfts.forEach((c) => {
      if (c.contractAddress && c.tokenId != null) {
        cardByToken.set(
          `${String(c.contractAddress).toLowerCase()}:${String(c.tokenId)}`,
          c,
        );
      }
    });

    // Enrich each crypto listing that also has a live "both" card rail, so
    // the tile can show "or $Y by card" and a Crypto-or-Card badge. Crypto-
    // only pieces pass through unchanged.
    const enrichedBlockchain = blockchainNfts.map((nft) => {
      const card = cardByToken.get(
        `${String(nft.contractAddress || "").toLowerCase()}:${String(nft.tokenId)}`,
      );
      if (card && card.saleRail === "both") {
        return { ...nft, cardEnabled: true, cardPriceUsd: card.priceUsd };
      }
      return nft;
    });

    // Only card-ONLY listings render as their own tiles; "both" pieces have
    // already been merged onto their crypto tile above.
    const cardOnly = cardListingNfts.filter((c) => c.saleRail !== "both");

    return [...enrichedBlockchain, ...cardOnly];
  }, [blockchainNfts, cardListingNfts]);

  // Client-side pagination for on-chain items - use spread+sort for immutability
  const paginatedOnChainItems = useMemo(() => {
    const start = (onChainPage - 1) * ITEMS_PER_PAGE;
    return [...onChainItems]
      .sort((a, b) => sortComparator(a, b, appliedFilters.sortBy))
      .slice(start, start + ITEMS_PER_PAGE);
  }, [onChainItems, onChainPage, appliedFilters.sortBy, sortComparator]);

  const onChainTotalPages = Math.ceil(onChainItems.length / ITEMS_PER_PAGE);

  // --- Handlers ---

  const handleChangeInput = async (e) => {
    try {
      let temp = { ...filters };
      const { name, value } = e.target;

      // Handle price inputs with validation
      if (name === "priceMin" || name === "priceMax") {
        // Allow empty string or valid numbers
        if (value === "" || (!isNaN(value) && parseFloat(value) >= 0)) {
          temp[name] = value;
        }
      } else {
        temp[name] = value;
      }

      setFilters(temp);
    } catch (e) {
      // Silent error handling for input changes
    }
  };

  const handleCheckBox = (e) => {
    try {
      let temp = { ...filters };
      if (e.target.checked) {
        temp[e.target.name] = e.target.value;
      } else {
        temp[e.target.name] = "";
      }
      setFilters(temp);
    } catch (e) {
      // Silent error handling for input changes
    }
  };

  const applyFilters = useCallback(async () => {
    try {
      setAppliedFilters({ ...filters });
      setFiltersChanged(false);
      setCurrentPage(1);
    } catch (e) {
      // Silent error handling for input changes
    }
  }, [filters]);

  const onSubmit = (event) => {
    try {
      event.preventDefault();
      applyFilters();
    } catch (e) {
      // Silent error handling for input changes
    }
  };

  // Handle Enter key press for quick apply
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const clearAll = async () => {
    try {
      setFilters(initialFilters);
      setAppliedFilters(initialFilters);
      setFiltersChanged(false);
      setCurrentPage(1);
    } catch (e) {
      // Silent error handling for input changes
    }
  };

  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    // Optional: Scroll to top when changing page
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleOnChainPageChange = useCallback((pageNumber) => {
    setOnChainPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleTabChange = useCallback((tabIndex) => {
    setActiveTab(tabIndex);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleViewToggle = (view) => {
    setViewMode(view);
  };

  const handleSortChange = useCallback((sortValue) => {
    // Update sort in both filters and appliedFilters, reset to page 1
    // This triggers the useEffect to refetch with the new sort from the server
    setFilters((prev) => ({ ...prev, sortBy: sortValue }));
    setAppliedFilters((prev) => ({ ...prev, sortBy: sortValue }));
    setCurrentPage(1);
  }, []);

  // --- Filtering Logic ---
  // Client-side filtering is removed as filtering is now done via API parameters
  // const filteredNfts = useMemo(() => { ... }, [nfts, activeFilters]);
  // We directly use the 'nfts' state which is fetched based on 'activeFilters'

  // --- Pagination Logic ---
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // Handle blockchain NFT purchase
  const handleBlockchainPurchase = async (nft) => {
    if (!isWalletConnected) {
      errorToast("Please connect your wallet to purchase Impact Arts");
      return;
    }

    // Reserved (a card checkout of this dual-rail piece is in flight):
    // don't send the buyer into a guaranteed revert.
    if (nft.locked) {
      errorToast(
        "Reserved — a card purchase of this piece is in progress. Check back in a little while.",
      );
      return;
    }

    // Check if user is trying to buy their own NFT
    if (
      walletAddress &&
      ((nft.seller &&
        typeof nft.seller === "string" &&
        walletAddress.toLowerCase() === nft.seller.toLowerCase()) ||
        (nft.owner &&
          typeof nft.owner === "string" &&
          walletAddress.toLowerCase() === nft.owner.toLowerCase()) ||
        (nft.creator?.walletAddress &&
          typeof nft.creator.walletAddress === "string" &&
          walletAddress.toLowerCase() ===
            nft.creator.walletAddress.toLowerCase()))
    ) {
      errorToast("You cannot purchase your own Impact Art");
      return;
    }

    try {
      setPurchasing(true);
      setPurchaseModal(nft);

      const transaction = await buyNFT({
        tokenId: nft.tokenId,
        price: nft.priceEth,
        contractAddress: nft.contractAddress,
      });

      // Record the purchase in the backend, same payload as the detail
      // page. Non-blocking — the purchase already happened on-chain — but
      // it's also what closes a dual-rail piece's card listing after its
      // crypto rail sold it, so it must fire from this path too.
      try {
        await recordBlockchainPurchase({
          tokenId: nft.tokenId,
          contractAddress: nft.contractAddress || "",
          transactionHash: transaction.hash,
          price: nft.priceEth,
          buyerWalletAddress: walletAddress,
          sellerWalletAddress: nft.seller || nft.creator?.walletAddress,
          campaignWalletAddress: nft.campaignOwner || null,
          causeWalletAddress: nft.causeOwner || null,
          impactPercentage: nft.rawImpactPercentage || 2500,
          nftName: nft.itemName,
          nftImage: nft.imageUrl,
          impactInitiativeName: nft.impactInitiativeName || "",
        });
      } catch (recordError) {
        console.error(
          "Failed to record blockchain purchase in backend:",
          recordError,
        );
      }

      successToast(
        "Impact Art purchased successfully! Transaction: " +
          transaction.hash.slice(0, 10) +
          "...",
      );

      // Refresh blockchain NFTs after purchase
      await fetchBlockchainNFTs();
    } catch (error) {
      // buyNFT throws buyer-ready messages; anything rawer (wallet/provider
      // errors) maps to friendly copy. Raw error is logged by the mapper.
      errorToast(mapWeb3Error(error));
    } finally {
      setPurchasing(false);
      setPurchaseModal(null);
    }
  };

  // --- Render Logic ---

  // Loading State
  if (loading && nfts.length === 0) {
    return (
      <>
        <Meta title="Impact Art Marketplace || Impactoverse" />
        <main className="min-h-screen bg-jacarta-50 dark:bg-jacarta-900 pt-20">
          {/* Hero Section */}
          <section className="bg-white dark:bg-jacarta-800 border-b border-jacarta-100 dark:border-jacarta-600">
            <div
              className="container mx-auto"
              style={{ padding: 'clamp(1.5rem, 4vw, 2.5rem) clamp(1rem, 3vw, 2rem)' }}
            >
              <div className="text-center">
                <h1
                  className="font-bold text-jacarta-700 dark:text-white"
                  style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}
                >
                  Impact Art Marketplace
                </h1>
                <p
                  className="text-jacarta-500 dark:text-jacarta-300 mt-2"
                  style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}
                >
                  Discover Impact Art that creates positive change
                </p>
              </div>
            </div>
          </section>

          {/* Main Content with Sidebar Layout */}
          <section style={{ padding: 'clamp(1.5rem, 4vw, 2.5rem) 0' }}>
            <div
              className="container mx-auto"
              style={{ padding: '0 clamp(1rem, 3vw, 2rem)' }}
            >
              <div
                className="flex flex-col lg:flex-row"
                style={{ gap: 'clamp(1rem, 3vw, 1.5rem)' }}
              >
                {/* Skeleton Sidebar */}
                <div className="hidden lg:block lg:w-64 lg:flex-shrink-0">
                  <div
                    className="bg-white dark:bg-jacarta-700 rounded-xl sm:rounded-2xl border border-jacarta-100 dark:border-jacarta-600"
                    style={{ padding: 'clamp(1rem, 3vw, 1.5rem)' }}
                  >
                    {/* Search skeleton */}
                    <div className="mb-5">
                      <div className="h-4 bg-grayish/20 dark:bg-jacarta-600 rounded w-20 mb-2 animate-pulse"></div>
                      <div className="h-10 bg-grayish/20 dark:bg-jacarta-600 rounded-lg animate-pulse"></div>
                    </div>

                    {/* Text filters skeleton */}
                    <div className="mb-5">
                      <div className="h-4 bg-grayish/20 dark:bg-jacarta-600 rounded w-16 mb-2 animate-pulse"></div>
                      <div className="h-10 bg-grayish/20 dark:bg-jacarta-600 rounded-lg animate-pulse"></div>
                    </div>

                    <div className="mb-5">
                      <div className="h-4 bg-grayish/20 dark:bg-jacarta-600 rounded w-20 mb-2 animate-pulse"></div>
                      <div className="h-10 bg-grayish/20 dark:bg-jacarta-600 rounded-lg animate-pulse"></div>
                    </div>

                    {/* Categories skeleton */}
                    <div className="mb-5">
                      <div className="h-4 bg-grayish/20 dark:bg-jacarta-600 rounded w-28 mb-2 animate-pulse"></div>
                      <div className="h-10 bg-grayish/20 dark:bg-jacarta-600 rounded-lg animate-pulse"></div>
                    </div>

                    <div className="mb-5">
                      <div className="h-4 bg-grayish/20 dark:bg-jacarta-600 rounded w-32 mb-2 animate-pulse"></div>
                      <div className="h-10 bg-grayish/20 dark:bg-jacarta-600 rounded-lg animate-pulse"></div>
                    </div>

                    {/* Price filters skeleton */}
                    <div className="mb-5">
                      <div className="h-4 bg-grayish/20 dark:bg-jacarta-600 rounded w-24 mb-2 animate-pulse"></div>
                      <div className="flex gap-2">
                        <div className="h-10 bg-grayish/20 dark:bg-jacarta-600 rounded-lg flex-1 animate-pulse"></div>
                        <div className="h-10 bg-grayish/20 dark:bg-jacarta-600 rounded-lg flex-1 animate-pulse"></div>
                      </div>
                    </div>

                    {/* Buttons skeleton */}
                    <div className="flex gap-2 pt-2">
                      <div className="h-10 bg-grayish/20 dark:bg-jacarta-600 rounded-lg flex-1 animate-pulse"></div>
                      <div className="h-10 bg-grayish/20 dark:bg-jacarta-600 rounded-lg w-16 animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* Skeleton Content Area */}
                <div className="flex-1 min-w-0">
                  {/* Header skeleton */}
                  <div
                    className="bg-white dark:bg-jacarta-800 rounded-xl sm:rounded-2xl border border-jacarta-100 dark:border-jacarta-600"
                    style={{ padding: 'clamp(1rem, 3vw, 1.5rem)', marginBottom: 'clamp(1rem, 3vw, 1.5rem)' }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="h-6 bg-grayish/20 dark:bg-jacarta-600 rounded w-24 animate-pulse"></div>
                      <div className="flex gap-3">
                        <div className="h-9 bg-grayish/20 dark:bg-jacarta-600 rounded-lg w-32 animate-pulse"></div>
                        <div className="h-9 bg-grayish/20 dark:bg-jacarta-600 rounded-lg w-20 animate-pulse"></div>
                      </div>
                    </div>
                  </div>

                  {/* NFT Grid skeleton - matches actual grid layout */}
                  <div
                    className="grid"
                    style={{
                      gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
                      gap: 'clamp(1rem, 3vw, 2rem)'
                    }}
                  >
                    {Array.from({ length: 12 }).map((_, index) => (
                      <SkeletonNFTCard key={index} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </>
    );
  }

  // Error State
  if (error) {
    return (
      <>
        <Meta title="Impact Art Marketplace || Impactoverse" />
        <main className="min-h-screen bg-jacarta-50 dark:bg-jacarta-900 pt-20">
          {/* Hero Section */}
          <section className="bg-white dark:bg-jacarta-800 border-b border-jacarta-100 dark:border-jacarta-600">
            <div
              className="container mx-auto"
              style={{ padding: 'clamp(1.5rem, 4vw, 2.5rem) clamp(1rem, 3vw, 2rem)' }}
            >
              <div className="text-center">
                <h1
                  className="font-bold text-jacarta-700 dark:text-white"
                  style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}
                >
                  Impact Art Marketplace
                </h1>
                <p
                  className="text-jacarta-500 dark:text-jacarta-300 mt-2"
                  style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}
                >
                  Discover Impact Art that creates positive change
                </p>
              </div>
            </div>
          </section>

          {/* Error Content */}
          <section style={{ padding: 'clamp(1.5rem, 4vw, 2.5rem) 0' }}>
            <div
              className="container mx-auto"
              style={{ padding: '0 clamp(1rem, 3vw, 2rem)' }}
            >
              <div className="max-w-lg mx-auto">
                <div
                  className="bg-white dark:bg-jacarta-800 rounded-xl sm:rounded-2xl border border-jacarta-100 dark:border-jacarta-600 text-center"
                  style={{ padding: 'clamp(2rem, 6vw, 3rem) clamp(1.5rem, 4vw, 2rem)' }}
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 sm:w-10 sm:h-10 text-red-500 dark:text-red-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.084 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <h3
                    className="font-semibold text-jacarta-700 dark:text-white mb-2"
                    style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}
                  >
                    Error Loading Marketplace
                  </h3>
                  <p className="text-sm sm:text-base text-jacarta-500 dark:text-jacarta-400 mb-6">
                    {error}
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-3 sm:py-2.5 bg-accent text-white rounded-full sm:rounded-lg font-medium hover:bg-accent-dark active:scale-95 transition-all"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </section>
        </main>
      </>
    );
  }

  // Main Content
  return (
    <>
      <Meta title="Impact Art Marketplace || Impactoverse" />

      <main className="min-h-screen bg-jacarta-50 dark:bg-jacarta-900 pt-20">
        {/* Hero Section */}
        <section className="bg-white dark:bg-jacarta-800 border-b border-jacarta-100 dark:border-jacarta-600">
          <div
            className="container mx-auto"
            style={{ padding: 'clamp(1.5rem, 4vw, 2.5rem) clamp(1rem, 3vw, 2rem)' }}
          >
            <div className="text-center">
              <h1
                className="font-bold text-jacarta-700 dark:text-white"
                style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}
              >
                Impact Art Marketplace
              </h1>
              <p
                className="text-jacarta-500 dark:text-jacarta-300 mt-2"
                style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}
              >
                Discover Impact Art that creates positive change
              </p>
              <div
                className="flex flex-wrap items-center justify-center text-jacarta-500 dark:text-jacarta-400 mt-4"
                style={{ gap: 'clamp(0.75rem, 2vw, 1.5rem)', fontSize: 'clamp(0.7rem, 1.5vw, 0.75rem)' }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${isWalletConnected ? "bg-green-500" : "bg-red-500"}`}
                  ></div>
                  <span>
                    Wallet:{" "}
                    {isWalletConnected ? "Connected" : "Not Connected"}
                  </span>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="flex justify-center mt-6">
                <div className="inline-flex bg-jacarta-100 dark:bg-jacarta-900 rounded-xl p-1.5 border border-transparent dark:border-jacarta-600">
                  <button
                    onClick={() => handleTabChange(0)}
                    className={`px-4 sm:px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${
                      activeTab === 0
                        ? "bg-white dark:bg-accent text-accent dark:text-white shadow-sm"
                        : "text-jacarta-600 dark:text-jacarta-200 hover:text-accent dark:hover:text-white"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${activeTab === 0 ? "bg-green-400" : "bg-green-500"}`}></span>
                      Impact Art
                      <span className={activeTab === 0 ? "text-accent/70 dark:text-white/70" : "text-jacarta-400 dark:text-jacarta-400"}>
                        ({totalItems.toLocaleString()})
                      </span>
                    </span>
                  </button>
                  <button
                    onClick={() => handleTabChange(1)}
                    className={`px-4 sm:px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${
                      activeTab === 1
                        ? "bg-white dark:bg-accent text-accent dark:text-white shadow-sm"
                        : "text-jacarta-600 dark:text-jacarta-200 hover:text-accent dark:hover:text-white"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${activeTab === 1 ? "bg-purple-300" : "bg-purple-500"}`}></span>
                      On-Chain
                      <span className={activeTab === 1 ? "text-accent/70 dark:text-white/70" : "text-jacarta-400 dark:text-jacarta-400"}>
                        ({onChainItems.length.toLocaleString()})
                      </span>
                      {blockchainLoading && (
                        <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></span>
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section style={{ padding: 'clamp(1.5rem, 4vw, 2.5rem) 0' }}>
          <div
            className="container mx-auto"
            style={{ padding: '0 clamp(1rem, 3vw, 2rem)' }}
          >
            <div
              className="flex flex-col lg:flex-row"
              style={{ gap: 'clamp(1rem, 3vw, 1.5rem)' }}
            >
              {/* Mobile Filter Toggle */}
              <div className="lg:hidden">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-jacarta-700 border border-jacarta-100 dark:border-jacarta-600 rounded-xl text-jacarta-700 dark:text-white hover:border-accent transition-colors font-medium"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                    />
                  </svg>
                  {showFilters ? "Hide Filters" : "Show Filters"}
                  {filtersChanged && (
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                  )}
                </button>
              </div>

              {/* Sidebar - Desktop always visible, Mobile toggleable */}
              <div
                className={`lg:w-64 lg:flex-shrink-0 ${showFilters ? "block" : "hidden lg:block"}`}
              >
                <Sidebar
                  handleChangeInput={handleChangeInput}
                  handleCheckBox={handleCheckBox}
                  filters={filters}
                  onSubmit={onSubmit}
                  applyFilters={applyFilters}
                  filtersChanged={filtersChanged}
                  handleKeyPress={handleKeyPress}
                  impactCategory={impactCategories}
                  nftCategory={nftCategories}
                  clearAll={clearAll}
                />
              </div>

              {/* Content Area */}
              <div className="flex-1 min-w-0">
                {/* Content Header */}
                <div
                  className="bg-white dark:bg-jacarta-800 rounded-xl sm:rounded-2xl border border-jacarta-100 dark:border-jacarta-600"
                  style={{ padding: 'clamp(1rem, 3vw, 1.5rem)', marginBottom: 'clamp(1rem, 3vw, 1.5rem)' }}
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2
                        className="font-semibold text-jacarta-700 dark:text-white"
                        style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)' }}
                      >
                        {activeTab === 0
                          ? `${totalItems.toLocaleString()} Impact Art`
                          : `${onChainItems.length.toLocaleString()} On-Chain Impact Arts`}
                      </h2>
                      {activeTab === 1 && blockchainLoading && (
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-jacarta-500 dark:text-jacarta-400">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                          <span className="hidden xs:inline">Loading on-chain Impact Arts...</span>
                          <span className="xs:hidden">Loading...</span>
                        </div>
                      )}
                    </div>

                    {/* Mobile: Stack vertically, Desktop: Side by side */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                      {/* Sort Filter */}
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-jacarta-600 dark:text-jacarta-300 whitespace-nowrap">
                          Sort:
                        </label>
                        <div className="flex-1 sm:flex-initial">
                          <MarketplaceSortFilter
                            onSortChange={handleSortChange}
                            currentSort={appliedFilters.sortBy}
                          />
                        </div>
                      </div>

                      {/* View Toggle */}
                      <div className="flex justify-center sm:justify-start">
                        <div className="flex bg-jacarta-100 dark:bg-jacarta-600 rounded-lg p-1">
                          <button
                            onClick={() => handleViewToggle("grid")}
                            className={`flex items-center justify-center w-8 h-8 rounded-md transition-all ${
                              viewMode === "grid"
                                ? "bg-white shadow-sm text-accent"
                                : "text-jacarta-600 dark:text-jacarta-400 hover:text-accent"
                            }`}
                            title="Grid View"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={16}
                              height={16}
                              className="fill-current"
                            >
                              <path d="M3 3H11V11H3V3ZM3 13H11V21H3V13ZM13 3H21V11H13V3ZM13 13H21V21H13V13Z" />
                            </svg>
                          </button>

                          <button
                            onClick={() => handleViewToggle("list")}
                            className={`flex items-center justify-center w-8 h-8 rounded-md transition-all ${
                              viewMode === "list"
                                ? "bg-white shadow-sm text-accent"
                                : "text-jacarta-600 dark:text-jacarta-400 hover:text-accent"
                            }`}
                            title="List View"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={16}
                              height={16}
                              className="fill-current"
                            >
                              <path d="M11 4H21V6H11V4ZM11 8H17V10H11V8ZM11 14H21V16H11V14ZM11 18H17V20H11V18ZM3 4H9V10H3V4ZM3 14H9V20H3V14Z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Content Area */}
                {loading && nfts.length === 0 ? (
                  // Full skeleton loading state
                  viewMode === "grid" ? (
                    <div
                      className="grid"
                      style={{
                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
                        gap: 'clamp(1rem, 3vw, 2rem)'
                      }}
                    >
                      {Array.from({ length: 12 }).map((_, index) => (
                        <SkeletonNFTCard key={index} />
                      ))}
                    </div>
                  ) : (
                    <div className="w-full">
                      {/* Desktop Table Skeleton */}
                      <div className="hidden xl:block">
                        <div className="scrollbar-custom overflow-x-auto">
                          <div className="w-full min-w-[900px] bg-white dark:bg-jacarta-800 text-sm dark:text-white rounded-xl shadow-sm border border-jacarta-100 dark:border-jacarta-600">
                            {/* Table Header */}
                            <div className="flex bg-jacarta-50 dark:bg-jacarta-700 rounded-t-xl border-b border-jacarta-100 dark:border-jacarta-600">
                              <div className="w-[35%] py-3 px-4">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Impact Art
                                </span>
                              </div>
                              <div className="w-[13%] py-3 px-4 text-center">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Price
                                </span>
                              </div>
                              <div className="w-[13%] py-3 px-4 text-center">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Likes
                                </span>
                              </div>
                              <div className="w-[13%] py-3 px-4 text-center">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Creator
                                </span>
                              </div>
                              <div className="w-[13%] py-3 px-4 text-center">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Views
                                </span>
                              </div>
                              <div className="w-[13%] py-3 px-4 text-center">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Action
                                </span>
                              </div>
                            </div>
                            {/* Table Body Skeleton */}
                            {Array.from({ length: 8 }).map((_, index) => (
                              <SkeletonListItem
                                key={index}
                                viewType="desktop"
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Tablet Table Skeleton */}
                      <div className="hidden lg:block xl:hidden">
                        <div className="scrollbar-custom overflow-x-auto">
                          <div className="w-full min-w-[700px] bg-white dark:bg-jacarta-800 text-sm dark:text-white rounded-xl shadow-sm border border-jacarta-100 dark:border-jacarta-600">
                            {/* Compact Table Header */}
                            <div className="flex bg-jacarta-50 dark:bg-jacarta-700 rounded-t-xl border-b border-jacarta-100 dark:border-jacarta-600">
                              <div className="w-[45%] py-3 px-4">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Impact Art
                                </span>
                              </div>
                              <div className="w-[18%] py-3 px-4 text-center">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Price
                                </span>
                              </div>
                              <div className="w-[18%] py-3 px-4 text-center">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Stats
                                </span>
                              </div>
                              <div className="w-[19%] py-3 px-4 text-center">
                                <span className="font-semibold text-jacarta-700 dark:text-white">
                                  Action
                                </span>
                              </div>
                            </div>
                            {/* Compact Table Body Skeleton */}
                            {Array.from({ length: 8 }).map((_, index) => (
                              <SkeletonListItem key={index} viewType="tablet" />
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Mobile Card Skeleton */}
                      <div className="block lg:hidden">
                        {Array.from({ length: 6 }).map((_, index) => (
                          <SkeletonListItem key={index} viewType="mobile" />
                        ))}
                      </div>
                    </div>
                  )
                ) : loading && nfts.length > 0 ? (
                  <div className="flex items-center justify-center gap-3 py-8">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent border-t-transparent"></div>
                    <span className="text-jacarta-500 dark:text-jacarta-400">Updating results...</span>
                  </div>
                ) : activeTab === 0 ? (
                  // Impact Art Tab Content
                  normalizedDatabaseNfts.length > 0 ? (
                    viewMode === "grid" ? (
                      <div
                        className="grid"
                        style={{
                          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
                          gap: 'clamp(1rem, 3vw, 2rem)'
                        }}
                      >
                        {normalizedDatabaseNfts.map((nft, index) => (
                          <div key={nft._id} className="relative">
                            {/* NFT Type Badge */}
                            <div className="absolute top-2 left-2 z-10">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-300">
                                Impact Art
                              </span>
                            </div>

                            <NFTCard
                              nftId={nft._id}
                              imageUrl={nft.imageUrl}
                              itemName={nft.itemName}
                              priceUsd={nft.priceUsd}
                              priceEth={nft.priceEth}
                              creator={nft.creator}
                              viewsCount={nft.viewsCount}
                              favouritesCount={nft.favouritesCount}
                              index={index}
                              isBlockchain={false}
                              onBlockchainPurchase={() => {}}
                              seller={nft.seller}
                              owner={nft.owner}
                              ownerId={nft.ownerId}
                              walletAddress={walletAddress}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Collections
                        assetsList={normalizedDatabaseNfts}
                        viewMode={viewMode}
                        startIndex={(currentPage - 1) * ITEMS_PER_PAGE}
                        walletAddress={walletAddress}
                      />
                    )
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center text-center bg-white dark:bg-jacarta-800 rounded-xl sm:rounded-2xl border border-jacarta-100 dark:border-jacarta-600"
                      style={{ padding: 'clamp(2rem, 6vw, 4rem) clamp(1rem, 3vw, 2rem)' }}
                    >
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-jacarta-50 dark:bg-jacarta-700 flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 sm:w-10 sm:h-10 text-jacarta-300 dark:text-jacarta-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <p
                        className="font-semibold text-jacarta-700 dark:text-white mb-2"
                        style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}
                      >
                        No Impact Art found
                      </p>
                      <p className="text-sm sm:text-base text-jacarta-500 dark:text-jacarta-400 max-w-md">
                        {JSON.stringify(appliedFilters) !== JSON.stringify(initialFilters)
                          ? "Try adjusting your filters to find what you're looking for."
                          : "The marketplace is currently empty. Check back soon!"}
                      </p>
                      {JSON.stringify(appliedFilters) !== JSON.stringify(initialFilters) && (
                        <button
                          onClick={clearAll}
                          className="mt-6 px-6 py-2.5 bg-accent hover:bg-accent-dark text-white rounded-full font-medium transition-colors text-sm sm:text-base"
                        >
                          Clear All Filters
                        </button>
                      )}
                    </div>
                  )
                ) : (
                  // On-Chain Tab Content
                  paginatedOnChainItems.length > 0 ? (
                    viewMode === "grid" ? (
                      <div
                        className="grid"
                        style={{
                          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
                          gap: 'clamp(1rem, 3vw, 2rem)'
                        }}
                      >
                        {paginatedOnChainItems.map((nft, index) => (
                          <div key={nft._id} className="relative">
                            {/* NFT Type Badge */}
                            <div className="absolute top-2 left-2 z-10 flex gap-1">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-300">
                                On-Chain
                              </span>
                              {nft.cardEnabled && nft.cardPriceUsd != null && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-300">
                                  Crypto or Card
                                </span>
                              )}
                              {nft.isCardSale && nft.saleInProgress && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-800 dark:text-amber-300">
                                  Sale pending
                                </span>
                              )}
                              {!nft.isCardSale && nft.locked && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-800 dark:text-amber-300">
                                  Reserved
                                </span>
                              )}
                            </div>

                            <NFTCard
                              nftId={nft._id}
                              imageUrl={nft.imageUrl}
                              itemName={nft.itemName}
                              priceUsd={nft.priceUsd}
                              priceEth={nft.priceEth}
                              cardEnabled={nft.cardEnabled}
                              cardPriceUsd={nft.cardPriceUsd}
                              creator={nft.creator}
                              viewsCount={nft.viewsCount}
                              favouritesCount={nft.favouritesCount}
                              index={index}
                              isBlockchain={!nft.isCardSale}
                              onBlockchainPurchase={
                                nft.isCardSale
                                  ? undefined
                                  : () => handleBlockchainPurchase(nft)
                              }
                              seller={nft.seller}
                              owner={nft.owner}
                              ownerId={nft.ownerId}
                              walletAddress={walletAddress}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Collections
                        assetsList={paginatedOnChainItems}
                        viewMode={viewMode}
                        startIndex={(onChainPage - 1) * ITEMS_PER_PAGE}
                        walletAddress={walletAddress}
                      />
                    )
                  ) : blockchainLoading ? (
                    <div className="flex items-center justify-center gap-3 py-8">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent border-t-transparent"></div>
                      <span className="text-jacarta-500 dark:text-jacarta-400">Loading on-chain Impact Arts...</span>
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center text-center bg-white dark:bg-jacarta-800 rounded-xl sm:rounded-2xl border border-jacarta-100 dark:border-jacarta-600"
                      style={{ padding: 'clamp(2rem, 6vw, 4rem) clamp(1rem, 3vw, 2rem)' }}
                    >
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-jacarta-50 dark:bg-jacarta-700 flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 sm:w-10 sm:h-10 text-jacarta-300 dark:text-jacarta-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </div>
                      <p
                        className="font-semibold text-jacarta-700 dark:text-white mb-2"
                        style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}
                      >
                        No On-Chain Impact Arts found
                      </p>
                      <p className="text-sm sm:text-base text-jacarta-500 dark:text-jacarta-400 max-w-md">
                        No Impact Arts are currently listed on the blockchain marketplace.
                      </p>
                    </div>
                  )
                )}

                {/* Purchase Modal */}
                {purchaseModal && (
                  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
                    <div
                      className="bg-white dark:bg-jacarta-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md"
                      style={{ padding: 'clamp(1.25rem, 4vw, 1.5rem)' }}
                    >
                      <h3
                        className="font-semibold text-jacarta-700 dark:text-white mb-4"
                        style={{ fontSize: 'clamp(1rem, 2.5vw, 1.125rem)' }}
                      >
                        Purchasing Impact Art
                      </h3>
                      <div className="flex items-center gap-3 sm:gap-4 mb-4">
                        <img
                          src={purchaseModal.imageUrl}
                          alt={purchaseModal.itemName}
                          className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover flex-shrink-0"
                        />
                        <div>
                          <p className="font-medium text-jacarta-700 dark:text-white">
                            {purchaseModal.itemName}
                          </p>
                          <p className="text-sm text-jacarta-500 dark:text-jacarta-400">
                            {purchaseModal.priceEth} ETH
                          </p>
                        </div>
                      </div>
                      {purchasing ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-2 border-accent border-t-transparent"></div>
                          <p className="ml-3 text-sm sm:text-base text-jacarta-600 dark:text-jacarta-300">
                            Processing transaction...
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                          <button
                            onClick={() => setPurchaseModal(null)}
                            className="flex-1 px-4 py-3 sm:py-2.5 border border-jacarta-300 dark:border-jacarta-600 text-jacarta-700 dark:text-jacarta-300 rounded-xl sm:rounded-lg font-medium hover:bg-jacarta-50 dark:hover:bg-jacarta-700 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() =>
                              handleBlockchainPurchase(purchaseModal)
                            }
                            className="flex-1 px-4 py-3 sm:py-2.5 bg-accent hover:bg-accent-dark text-white rounded-xl sm:rounded-lg font-medium transition-colors"
                          >
                            Confirm Purchase
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Pagination for Both Grid and List Views */}
                {activeTab === 0 && !loading && totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
                {activeTab === 1 && !blockchainLoading && onChainTotalPages > 1 && (
                  <Pagination
                    currentPage={onChainPage}
                    totalPages={onChainTotalPages}
                    onPageChange={handleOnChainPageChange}
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default MarketplacePage;
